import * as Core from "@zeller-public/payments-sdk-core";

import { $postMessage } from "./post-message";
import { postResponse } from "./post-response";

export function Ok(): void {
	$postMessage(Core.Protocol.OK);
}
export const Debug = (
	data: Extract<Core.Protocol.Response, { $type: "Debug" }>["data"],
) => postResponse({ $type: "Debug", data });

export const Config = (
	config: Omit<Extract<Core.Protocol.Response, { $type: "Config" }>, "$type">,
) => postResponse({ $type: "Config", ...config });

export function Resolve<
	TQueryName extends Extract<
		Core.Protocol.Response,
		{ $type: "Result" }
	>["query"],
>(): ((
	response: Extract<
		Core.Protocol.Response,
		{ $type: "Result" | "ResultError" | "Event"; query?: TQueryName }
	>,
) => void) & {
	Ok: (
		value: Extract<
			Core.Protocol.Response,
			{ $type: "Result"; query?: TQueryName }
		>["value"],
	) => void;

	$Ok: (
		value: Extract<
			Core.Protocol.Response,
			{ $type: "Result"; query?: TQueryName }
		>["value"],
	) => Extract<Core.Protocol.Response, { $type: "Result"; query?: TQueryName }>;

	Error: (
		...args: Extract<
			Core.Protocol.Response,
			{ $type: "ResultError"; query?: TQueryName }
		>["value"] extends infer Value
			? Value extends { type: string }
				? [
						type: Value["type"],
						options: Omit<Value, "type"> & { message: string },
					]
				: never
			: never
	) => void;

	$Error: (
		...args: Extract<
			Core.Protocol.Response,
			{ $type: "ResultError"; query?: TQueryName }
		>["value"] extends infer Value
			? Value extends { type: string }
				? [
						type: Value["type"],
						options: Omit<Value, "type"> & { message: string },
					]
				: never
			: never
	) => Extract<
		Core.Protocol.Response,
		{ $type: "ResultError"; query?: TQueryName }
	>;

	Event: (
		type: Extract<
			Core.Protocol.Response,
			{ $type: "Event"; query?: TQueryName }
		>["value"]["event"],
		options?: { message?: string },
	) => void;
} {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return resolver as any;
}

Resolve.Ok = (...args: Parameters<typeof Resolve.$Ok>): void =>
	void postResponse(Resolve.$Ok(...args));

Resolve.$Ok = (
	value: Extract<Core.Protocol.Response, { $type: "Result" }>["value"],
): Exclude<Core.Protocol.Response, string> => ({ $type: "Result", value });

Resolve.Error = (...args: Parameters<typeof Resolve.$Error>): void =>
	void postResponse(Resolve.$Error(...args));

Resolve.$Error = (
	...args: Extract<
		Core.Protocol.Response,
		{ $type: "ResultError" }
	>["value"] extends infer Value
		? Value extends { type: string }
			? [
					type: Value["type"],
					options: Omit<Value, "type"> & { message: string },
				]
			: never
		: never
): Exclude<Core.Protocol.Response, string> => ({
	$type: "ResultError",
	value: { type: args[0], ...args[1] },
});

function Event(
	type: Extract<Core.Protocol.Response, { $type: "Event" }>["value"]["event"],
	options?: { message?: string },
): void {
	return void postResponse({
		$type: "Event",
		value: { event: type, ...options },
	});
}

const resolver = Object.assign(
	(...args: Parameters<typeof postResponse>) => postResponse(...args),
	{
		Ok: Resolve.Ok,
		$Ok: Resolve.$Ok,
		Error: Resolve.Error,
		$Error: Resolve.$Error,
		Event,
	},
);
